import React, {FC} from 'react';
import AuctionSeries from './components/AuctionSeriesView';
import {Observer, observer} from 'mobx-react-lite';
import AuctionRegionPagePresenter from './AuctionRegionPagePresenter';
import {CommonMainTitle} from '../components/commonMainTitle/CommonMainTitle';
import {HeaderAdviceList} from '../components/headerAdviceList/HeaderAdviceList';
import {Tabs} from '@components/Tabs';
import {TopHeading} from '@components/TopHeading';
import {Only} from '@joyrideautos/ui-common/src/components/Only';
import {Alert, Col, Empty, Row} from 'antd';
import illustrationEmpty from '@joyrideautos/ui-common/src/images/illustration_empty.svg';
import {PDFModal} from '../components/pdfModal/PDFModalWithViewModel';
import {isAuthenticated} from '@joyrideautos/auction-core/src/rules/UserModel';
import {Loading} from '@joyrideautos/web-common-components/src/components/loading/Loading';
import {usePresenter} from '@joyrideautos/web-common-components/src/hooks/usePresenter';

interface AuctionRegionPageProps {}

const AuctionRegionPage: FC<AuctionRegionPageProps> = observer(() => {
    const {presenter} = usePresenter(AuctionRegionPagePresenter);

    if (!presenter) {
        return null;
    }

    const {pdfModalViewModel, isLoading, error} = presenter;

    const authenticated = isAuthenticated(presenter.authUser);
    return (
        <>
            <PDFModal viewModel={pdfModalViewModel} />
            <Only when={!!error}>
                <Alert message={error} type="error" />
            </Only>
            <Only when={isLoading}>
                <Loading />
            </Only>
            <Only when={!isLoading && !error}>
                {() => {
                    const {region, upcomingAuctionSeries, pastAuctionSeries, getAuctionSeriesViewModel} = presenter;
                    return (
                        <>
                            <section className="section-tool">
                                <TopHeading
                                    left={
                                        <Observer>
                                            {() => {
                                                const toggleFavoriteHandler = authenticated
                                                    ? () => presenter.toggleFavorite()
                                                    : undefined;
                                                return (
                                                    <CommonMainTitle
                                                        title={presenter.title}
                                                        isFavorite={presenter.isFavorite}
                                                        toggleFavorite={toggleFavoriteHandler}
                                                        canToggleFavorite={!!toggleFavoriteHandler}
                                                    />
                                                );
                                            }}
                                        </Observer>
                                    }
                                />
                                <Row align="middle" className={'section-filter'}>
                                    <Col xs={24} sm={24} md={24} lg={16} xl={16}>
                                        <HeaderAdviceList
                                            policiesItems={
                                                region && region.policy_docs
                                                    ? region.policy_docs.map((doc) => ({
                                                          id: doc.name,
                                                          label: doc.name,
                                                          onClick: () =>
                                                              pdfModalViewModel.handleDocument(doc.name, doc.document),
                                                      }))
                                                    : []
                                            }
                                            paymentInfoItems={
                                                region && region.payment_info_docs
                                                    ? region.payment_info_docs.map((doc) => ({
                                                          id: doc.name,
                                                          label: doc.name,
                                                          onClick: () =>
                                                              pdfModalViewModel.handleDocument(doc.name, doc.document),
                                                      }))
                                                    : []
                                            }
                                        />
                                    </Col>
                                </Row>
                            </section>
                            <Tabs
                                animated={false}
                                tabs={[
                                    {
                                        tab: (
                                            <div
                                                className="auction-series__tab-wrap"
                                                data-testid="auctions-regions-series-page-upcoming-auctions-tab">
                                                Upcoming auctions
                                            </div>
                                        ),
                                        key: '1',
                                        pane: (
                                            <>
                                                <Only when={upcomingAuctionSeries.length === 0}>
                                                    <Empty
                                                        className="empty-vehicles"
                                                        image={illustrationEmpty}
                                                        imageStyle={{
                                                            height: 220,
                                                        }}
                                                        description={
                                                            <span>
                                                                There are currently no upcoming scheduled auctions in{' '}
                                                                {presenter.title}
                                                            </span>
                                                        }
                                                    />
                                                </Only>
                                                <Only when={upcomingAuctionSeries.length > 0}>
                                                    {() =>
                                                        presenter.upcomingAuctionSeries.map((series) => (
                                                            <AuctionSeries
                                                                key={series.id}
                                                                model={getAuctionSeriesViewModel(series, true)}
                                                                isAuthenticated={authenticated}
                                                            />
                                                        ))
                                                    }
                                                </Only>
                                            </>
                                        ),
                                    },
                                    {
                                        tab: (
                                            <div
                                                className="auction-series__tab-wrap"
                                                data-testid="auctions-regions-series-page-past-auctions-tab">
                                                Past auctions
                                            </div>
                                        ),
                                        key: '2',
                                        pane: (
                                            <>
                                                <Only when={pastAuctionSeries.length === 0}>
                                                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                                </Only>
                                                <Only when={pastAuctionSeries.length > 0}>
                                                    {() =>
                                                        presenter.pastAuctionSeries.map((serie) => (
                                                            <AuctionSeries
                                                                key={serie.id}
                                                                model={getAuctionSeriesViewModel(serie, false)}
                                                                isAuthenticated={authenticated}
                                                            />
                                                        ))
                                                    }
                                                </Only>
                                            </>
                                        ),
                                    },
                                ]}
                            />
                        </>
                    );
                }}
            </Only>
        </>
    );
});

export default AuctionRegionPage;
